import { Alert, AlertProps, TextField } from "@mui/material";

export const InsertPinInput = ({
  pin,
  onPinChange,
  onPinSelect,
  onFocus,
  onBlur,
  error,
  activePinIndex,
  isPinFocused,
  pinInputRef,
  onKeyDown,
  alert,
}: {
  pin: string;
  onPinChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPinSelect: () => void;
  onFocus: () => void;
  onBlur: () => void;
  error: string | null;
  activePinIndex: number;
  isPinFocused: boolean;
  pinInputRef: React.RefObject<HTMLInputElement>;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  alert?: {
    severity?: AlertProps["severity"];
    alertContent?: React.ReactNode;
  } | null;
}) => {
  const focusOnInput = () => {
    if (pinInputRef.current) {
      pinInputRef.current.focus();
      pinInputRef.current.setSelectionRange(pin.length, pin.length);
      onFocus();
      onPinSelect();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        style={{
          display: "flex",
          gap: "8px",
          position: "relative",
          justifyContent: "center",
        }}
      >
        {[0, 1, 2, 3].map((index) => {
          const isActive = activePinIndex === index && isPinFocused;

          let borderStyle: string;

          if (isActive) {
            borderStyle = "2px solid #1976d2";
          } else {
            borderStyle = "1px solid rgba(0, 0, 0, 0.23)";
          }

          return (
            <div
              key={index}
              onClick={focusOnInput}
              style={{
                width: "50px",
                height: "60px",
                border: borderStyle,
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "2rem",
                fontWeight: "500",
                position: "relative",
                backgroundColor: "#f5f5f5",
                transition: "border-color 0.2s",
                cursor: "text",
                zIndex: 1,
              }}
            >
              {pin[index] ? "•" : ""}
            </div>
          );
        })}
        <TextField
          inputRef={pinInputRef}
          type="password"
          inputMode="numeric"
          value={pin}
          onChange={onPinChange}
          onSelect={onPinSelect}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyUp={onPinSelect}
          onKeyDown={onKeyDown}
          onClick={onPinSelect}
          error={!!error}
          inputProps={{
            maxLength: 4,
            pattern: "[0-9]*",
            onSelect: onPinSelect,
            onKeyUp: onPinSelect,
            onClick: onPinSelect,
            style: {
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              opacity: 0,
              cursor: "text",
              outline: "none",
              border: "none",
            },
          }}
          autoFocus
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
            "& .MuiInputBase-root": {
              border: "none !important",
              "&::before": {
                display: "none !important",
              },
              "&::after": {
                display: "none !important",
              },
              "&:hover": {
                border: "none !important",
                "&::before": {
                  display: "none !important",
                },
                "&::after": {
                  display: "none !important",
                },
              },
              "&.Mui-focused": {
                border: "none !important",
                "&::before": {
                  display: "none !important",
                },
                "&::after": {
                  display: "none !important",
                },
              },
            },
            "& .MuiOutlinedInput-root": {
              height: "100%",
              border: "none !important",
              "& fieldset": {
                border: "none !important",
              },
              "&:focus": {
                outline: "none",
                border: "none !important",
                "& fieldset": {
                  border: "none !important",
                },
              },
              "&:hover": {
                border: "none !important",
                "& fieldset": {
                  border: "none !important",
                },
              },
            },
            "& .MuiFilledInput-root": {
              border: "none !important",
              "&::before": {
                display: "none !important",
              },
              "&::after": {
                display: "none !important",
              },
              "&:hover": {
                border: "none !important",
                "&::before": {
                  display: "none !important",
                },
                "&::after": {
                  display: "none !important",
                },
              },
              "&.Mui-focused": {
                border: "none !important",
                "&::before": {
                  display: "none !important",
                },
                "&::after": {
                  display: "none !important",
                },
              },
            },
            "& .MuiOutlinedInput-input": {
              padding: 0,
              textAlign: "center",
              fontSize: "1.5rem",
              letterSpacing: "58px",
              textIndent: "25px",
              outline: "none",
              "&:focus": {
                outline: "none",
              },
              "&::selection": {
                background: "transparent",
              },
              "&::-moz-selection": {
                background: "transparent",
              },
            },
          }}
        />
      </div>
      {alert?.severity && (
        <Alert
          severity={alert?.severity || "error"}
          sx={{ fontSize: "0.875rem", marginTop: "10px", maxWidth: "100%" }}
        >
          {alert?.alertContent}
        </Alert>
      )}
    </div>
  );
};

export default InsertPinInput;
